import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSliders } from 'react-icons/fi';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';

const FilterSidebar = ({ 
  isOpen, 
  onClose, 
  selectedFilters, 
  setSelectedFilters, 
  filterOptions, 
  applyFilters,
  clearAllFilters 
}) => {
  const [priceInputs, setPriceInputs] = useState({
    min: selectedFilters.priceRange[0],
    max: selectedFilters.priceRange[1]
  });

  useEffect(() => {
    setPriceInputs({
      min: selectedFilters.priceRange[0],
      max: selectedFilters.priceRange[1]
    });
  }, [selectedFilters.priceRange]);

  const handlePriceInputChange = (type, value) => {
    const numValue = parseInt(value) || 0;
    const newInputs = { ...priceInputs, [type]: numValue };
    
    // Ensure min doesn't exceed max and max doesn't go below min
    if (type === 'min' && numValue > priceInputs.max) {
      newInputs.min = priceInputs.max;
    } else if (type === 'max' && numValue < priceInputs.min) {
      newInputs.max = priceInputs.min;
    }

    setPriceInputs(newInputs);
    setSelectedFilters(prev => ({
      ...prev,
      priceRange: [newInputs.min, newInputs.max]
    }));
  };

  const handleFilterChange = (category, value) => {
    setSelectedFilters(prev => {
      if (value === 'All') {
        if (prev[category].includes('All')) {
          return prev;
        }
        return { ...prev, [category]: ['All'] };
      }
      
      let newValues = [...prev[category]];
      
      if (newValues.includes('All')) {
        newValues = [value];
      } else {
        if (newValues.includes(value)) {
          newValues = newValues.filter(item => item !== value);
          if (newValues.length === 0) {
            newValues = ['All'];
          }
        } else {
          newValues.push(value);
        }
      }
      
      return { ...prev, [category]: newValues };
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
          />

          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
          >
            <div className="sticky top-0 bg-gradient-to-r from-[#1a1a1a] to-[#2C3E50] text-white z-10 px-6 py-4 border-b flex justify-between items-center">
              <div className="flex items-center gap-2">
                <FiSliders className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Refine Selection</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-6">
              <div className="mb-8">
                <h3 className="text-sm font-medium mb-4 text-[#2C3E50] flex items-center gap-2">
                  Price Range
                  <span className="text-xs text-gray-500">(in ₹)</span>
                </h3>
                
                {/* Price Input Fields */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <input
                        type="number"
                        value={priceInputs.min}
                        onChange={(e) => handlePriceInputChange('min', e.target.value)}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="Min"
                        min={filterOptions.priceRange.min}
                        max={filterOptions.priceRange.max}
                      />
                    </div>
                  </div>
                  <span className="text-gray-400">to</span>
                  <div className="flex-1">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <input
                        type="number"
                        value={priceInputs.max}
                        onChange={(e) => handlePriceInputChange('max', e.target.value)}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="Max"
                        min={filterOptions.priceRange.min}
                        max={filterOptions.priceRange.max}
                      />
                    </div>
                  </div>
                </div>

                {/* Range Slider */}
                <div className="px-2">
                  <RangeSlider
                    min={filterOptions.priceRange.min}
                    max={filterOptions.priceRange.max}
                    step={filterOptions.priceRange.step}
                    value={selectedFilters.priceRange}
                    onInput={(value) => {
                      setSelectedFilters(prev => ({
                        ...prev,
                        priceRange: value
                      }));
                      setPriceInputs({
                        min: value[0],
                        max: value[1]
                      });
                    }}
                    className="range-slider-custom"
                  />
                </div>
              </div>

              {Object.entries(filterOptions).map(([category, options]) => {
                if (category === 'priceRange') return null;
                
                return (
                  <div key={category} className="mb-8">
                    <h3 className="text-sm font-medium mb-4 capitalize text-[#2C3E50] border-b pb-2">
                      {category.replace(/([A-Z])/g, ' $1').trim()}
                    </h3>
                    <div className="space-y-2 pl-1">
                      <label className="flex items-center p-2 hover:bg-gray-50 rounded-lg transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedFilters[category].includes('All')}
                          onChange={() => handleFilterChange(category, 'All')}
                          className="rounded border-gray-300 text-[#2C3E50] focus:ring-[#34495E]"
                        />
                        <span className="ml-3 text-sm text-gray-700">All</span>
                      </label>
                      {options.map((option) => (
                        <label key={option} className="flex items-center p-2 hover:bg-gray-50 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedFilters[category].includes(option)}
                            onChange={() => handleFilterChange(category, option)}
                            className="rounded border-gray-300 text-[#2C3E50] focus:ring-[#34495E]"
                          />
                          <span className="ml-3 text-sm text-gray-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              <div className="flex space-x-4">
                <button
                  onClick={clearAllFilters}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Reset All
                </button>
                <button
                  onClick={() => {
                    applyFilters();
                    onClose();
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-[#1a1a1a] to-[#2C3E50] text-white rounded-lg hover:from-[#2C3E50] hover:to-[#34495E] transition-all"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FilterSidebar;

















